import { FormEvent, useEffect, useState } from "react";
import {
  Agent,
  AgentCreated,
  AgentToken,
  AgentTokenCreated,
  User,
  api,
} from "../api";
import { useAuth } from "../auth";
import { ConfirmModal } from "../components/ConfirmModal";
import { UserRolesModal } from "../components/UserRolesModal";

const MCP_URL = `${window.location.origin}/mcp/`;

export function AgentsPage() {
  const { token } = useAuth();
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [name, setName] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formNotice, setFormNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  // One-time credential reveal (client_id + client_secret).
  const [revealed, setRevealed] = useState<AgentCreated | null>(null);

  const [rolesFor, setRolesFor] = useState<Agent | null>(null);
  const [tokensFor, setTokensFor] = useState<Agent | null>(null);
  const [confirm, setConfirm] = useState<{
    title: string;
    tone: "danger" | "primary";
    confirmLabel: string;
    body: React.ReactNode;
    onConfirm: () => Promise<void>;
  } | null>(null);

  const reload = () => {
    if (!token) return;
    api
      .listAgents(token)
      .then(setAgents)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setFormNotice(null);
    const n = name.trim();
    if (n.length < 2) {
      setFormNotice({ tone: "err", text: "Agent name must be at least 2 characters." });
      return;
    }
    setFormBusy(true);
    try {
      const created = await api.createAgent(token, { name: n });
      setRevealed(created);
      setName("");
      reload();
    } catch (err: unknown) {
      const message =
        typeof err === "object" && err && "message" in err
          ? String((err as { message?: string }).message)
          : String(err);
      setFormNotice({ tone: "err", text: message });
    } finally {
      setFormBusy(false);
    }
  };

  const onRotate = (a: Agent) => {
    if (!token) return;
    setConfirm({
      title: "Rotate client secret",
      tone: "primary",
      confirmLabel: "Rotate secret",
      body: (
        <p>
          Rotate the client secret for <strong>{a.name}</strong>? The current secret stops working
          immediately; the new one is shown only once.
        </p>
      ),
      onConfirm: async () => {
        setBusyId(a.id);
        try {
          const rotated = await api.rotateAgentClientSecret(token, a.id);
          setRevealed(rotated);
          reload();
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const onToggleDisabled = (a: Agent) => {
    if (!token) return;
    setBusyId(a.id);
    api
      .patchAgent(token, a.id, { disabled: !a.disabled })
      .then(reload)
      .catch((e) => setError(String(e?.message ?? e)))
      .finally(() => setBusyId(null));
  };

  const onDelete = (a: Agent) => {
    if (!token) return;
    setConfirm({
      title: "Delete agent",
      tone: "danger",
      confirmLabel: "Delete agent",
      body: (
        <p>
          Delete agent <strong>{a.name}</strong>? Its OAuth client, all PATs, and role assignments
          are removed. Any process using this agent stops working immediately. This cannot be undone.
        </p>
      ),
      onConfirm: async () => {
        setBusyId(a.id);
        try {
          await api.deleteAgent(token, a.id);
          reload();
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  // UserRolesModal expects a User; agents are users.
  const asUser = (a: Agent): User => ({
    id: a.id,
    username: a.name,
    is_admin: false,
    disabled: a.disabled,
  });

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-2 text-sm">
        <p className="text-slate-700">
          <strong>Agents</strong> are machine principals (scripts, backend AI agents) that call MCP
          non-interactively. They reuse the same Roles as users but get exports served from the VM
          via an <strong>authenticated</strong> endpoint — no public/presigned links. Authenticate
          with <strong>client credentials</strong> (recommended) or a <strong>PAT</strong>.
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600 w-28">MCP server URL</span>
          <CopyField value={MCP_URL} mono />
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      {revealed && (
        <div className="card p-4 space-y-3 border-2 border-emerald-300 bg-emerald-50/60">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-emerald-900">
              Copy the client secret now — it is shown only once
            </h3>
            <button type="button" className="btn" onClick={() => setRevealed(null)}>
              Done
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600 w-28">Client ID</span>
            <CopyField value={revealed.client_id ?? ""} mono />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600 w-28">Client Secret</span>
            <CopyField value={revealed.client_secret ?? ""} mono />
          </div>
          <p className="text-xs text-emerald-800">
            Get a token with the OAuth <code>client_credentials</code> grant, then call MCP with it.
            Assign Roles to grant database access. If the secret is lost, rotate it.
          </p>
        </div>
      )}

      <div className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Create agent</h3>
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void onCreate(e)}>
          <div className="flex-1 min-w-[14rem]">
            <label className="block text-xs font-medium text-slate-600 mb-1">Agent name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="datamart-analytics-agent"
              required
            />
          </div>
          <button type="submit" className="btn-primary" disabled={formBusy}>
            {formBusy ? "Creating…" : "Create agent"}
          </button>
        </form>
        {formNotice && (
          <div
            className={`text-sm rounded px-3 py-2 border ${
              formNotice.tone === "ok"
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-red-50 text-red-800 border-red-200"
            }`}
          >
            {formNotice.text}
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        {agents === null ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : agents.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">
            No agents yet. Create one above, assign Roles, then hand the client credentials (or a
            PAT) to the agent.
          </p>
        ) : (
          <table className="table-clean table-fixed w-full">
            <thead>
              <tr>
                <th className="w-10">id</th>
                <th className="w-44">name</th>
                <th className="w-56">client_id</th>
                <th className="w-16">roles</th>
                <th className="w-16">PATs</th>
                <th className="w-20">status</th>
                <th className="text-right">actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 align-middle">
                  <td className="font-mono">{a.id}</td>
                  <td className="truncate" title={a.name}>{a.name}</td>
                  <td className="text-xs">
                    <CopyField value={a.client_id ?? "—"} mono compact />
                  </td>
                  <td className="text-xs">{a.role_count}</td>
                  <td className="text-xs">{a.active_token_count}</td>
                  <td className="text-xs whitespace-nowrap">
                    {a.disabled ? <span className="text-red-600">disabled</span> : "active"}
                  </td>
                  <td className="text-right space-x-1.5 whitespace-nowrap pr-4">
                    <button type="button" className="btn" disabled={busyId === a.id} onClick={() => setRolesFor(a)}>
                      roles
                    </button>
                    <button type="button" className="btn" disabled={busyId === a.id} onClick={() => setTokensFor(a)}>
                      tokens
                    </button>
                    <button type="button" className="btn" disabled={busyId === a.id} onClick={() => onRotate(a)}>
                      rotate
                    </button>
                    <button type="button" className="btn" disabled={busyId === a.id} onClick={() => onToggleDisabled(a)}>
                      {a.disabled ? "enable" : "disable"}
                    </button>
                    <button type="button" className="btn-danger" disabled={busyId === a.id} onClick={() => onDelete(a)}>
                      delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {token && rolesFor && (
        <UserRolesModal
          token={token}
          user={asUser(rolesFor)}
          onClose={() => setRolesFor(null)}
          onSaved={reload}
        />
      )}

      {token && tokensFor && (
        <AgentTokensModal
          token={token}
          agent={tokensFor}
          onClose={() => {
            setTokensFor(null);
            reload();
          }}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          tone={confirm.tone}
          confirmLabel={confirm.confirmLabel}
          body={confirm.body}
          onClose={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
        />
      )}
    </div>
  );
}

function AgentTokensModal({
  token,
  agent,
  onClose,
}: {
  token: string;
  agent: Agent;
  onClose: () => void;
}) {
  const [tokens, setTokens] = useState<AgentToken[] | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<AgentTokenCreated | null>(null);

  const reload = () => {
    api
      .listAgentTokens(token, agent.id)
      .then(setTokens)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token, agent.id]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.createAgentToken(token, agent.id, { label: label.trim() || null });
      setRevealed(created);
      setLabel("");
      reload();
    } catch (err: unknown) {
      setError(typeof err === "object" && err && "message" in err ? String((err as { message?: string }).message) : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (id: number) => {
    setBusy(true);
    try {
      await api.revokeAgentToken(token, agent.id, id);
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-lg w-full flex flex-col max-h-[85vh]"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">PATs — {agent.name}</h3>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <p className="text-xs text-slate-600">
            A PAT is a long-lived bearer token the agent sends directly. Prefer client credentials
            when possible; PATs are easier but more leakable. Revoke any time.
          </p>

          {revealed && (
            <div className="rounded border-2 border-emerald-300 bg-emerald-50/60 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-emerald-900">Copy this token now — shown once</span>
                <button type="button" className="btn" onClick={() => setRevealed(null)}>
                  Done
                </button>
              </div>
              <CopyField value={revealed.token ?? ""} mono />
            </div>
          )}

          <form className="flex items-end gap-2" onSubmit={(e) => void onCreate(e)}>
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 mb-1">Label (optional)</label>
              <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ci-pipeline" />
            </div>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "…" : "Create PAT"}
            </button>
          </form>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}

          {tokens === null ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : tokens.length === 0 ? (
            <p className="text-sm text-slate-500">No PATs yet.</p>
          ) : (
            <table className="table-clean w-full text-xs">
              <thead>
                <tr>
                  <th>label</th>
                  <th>expires</th>
                  <th>status</th>
                  <th className="text-right">actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 align-middle">
                    <td className="truncate" title={t.label ?? ""}>{t.label ?? "—"}</td>
                    <td>{t.expires_at ? new Date(t.expires_at).toLocaleDateString() : "never"}</td>
                    <td>{t.revoked ? <span className="text-red-600">revoked</span> : "active"}</td>
                    <td className="text-right">
                      {!t.revoked && (
                        <button type="button" className="btn-danger" disabled={busy} onClick={() => void onRevoke(t.id)}>
                          revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function CopyField({ value, mono = false, compact = false }: { value: string; mono?: boolean; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard may be blocked */
    }
  };
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <code
        className={`${mono ? "font-mono" : ""} ${compact ? "max-w-[14rem] truncate" : ""} text-[12px] text-slate-700 bg-slate-100 rounded px-2 py-1`}
        title={value}
      >
        {value || "—"}
      </code>
      <button type="button" onClick={() => void onCopy()} className="btn text-[10px] py-0.5 px-1.5">
        {copied ? "copied" : "copy"}
      </button>
    </span>
  );
}
